import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { BRANCHES, GRADE_LEVELS } from '@/mocks/mockData';

type ApplicationStatus =
  | 'Yeni Başvuru' | 'Öğretmen Aranıyor' | 'Öğretmene Yönlendirildi'
  | 'Deneme Dersi Planlandı' | 'Deneme Dersi Yapıldı' | 'Aktif Derse Başladı'
  | 'Takipte' | 'Devam Etmiyor' | 'İptal';

interface Basvuru {
  id: string;
  date: string;
  studentName: string;
  parentName: string;
  phone: string;
  city: string;
  grade: string;
  branch: string;
  lessonType: string;
  source: string;
  status: ApplicationStatus;
  target: string;
  notes: string;
}

const statusColors: Record<string, string> = {
  'Yeni Başvuru': 'bg-blue-100 text-blue-700',
  'Öğretmen Aranıyor': 'bg-orange-100 text-orange-700',
  'Öğretmene Yönlendirildi': 'bg-purple-100 text-purple-700',
  'Deneme Dersi Planlandı': 'bg-yellow-100 text-yellow-700',
  'Deneme Dersi Yapıldı': 'bg-cyan-100 text-cyan-700',
  'Aktif Derse Başladı': 'bg-green-100 text-green-700',
  'Takipte': 'bg-gray-100 text-gray-700',
  'Devam Etmiyor': 'bg-red-100 text-red-700',
  'İptal': 'bg-red-200 text-red-800',
};

const sourceColors: Record<string, string> = {
  Instagram: 'bg-pink-100 text-pink-700',
  WhatsApp: 'bg-green-100 text-green-700',
  Tavsiye: 'bg-amber-100 text-amber-700',
  Manuel: 'bg-gray-100 text-gray-600',
  Reklam: 'bg-indigo-100 text-indigo-700',
};

const ALL_STATUSES: ApplicationStatus[] = [
  'Yeni Başvuru', 'Öğretmen Aranıyor', 'Öğretmene Yönlendirildi',
  'Deneme Dersi Planlandı', 'Deneme Dersi Yapıldı', 'Aktif Derse Başladı',
  'Takipte', 'Devam Etmiyor', 'İptal',
];

const emptyForm = {
  studentName: '', parentName: '', phone: '', city: 'Antalya',
  grade: '', branch: '', target: '', lessonType: 'Birebir Online',
  source: 'WhatsApp', notes: '', status: 'Yeni Başvuru' as ApplicationStatus,
};

function lessonTypeToDb(label: string): string | null {
  switch (label) {
    case 'Birebir Online': return 'bireysel_online';
    case 'Yüz Yüze': return 'yuz_yuze';
    case 'Grup Dersi': return 'grup';
    case 'KPSS': return 'kpss';
    default: return null;
  }
}

export default function BasvurularPage() {
  const [data, setData] = useState<Basvuru[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [detailItem, setDetailItem] = useState<Basvuru | null>(null);
  const [toast, setToast] = useState('');

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data: rows, error } = await supabase
      .from('egtpanel_basvurular')
      .select('*')
      .order('basvuru_tarihi', { ascending: false });

    if (!error && rows) {
      setData(rows.map((r) => ({
        id: r.id,
        date: r.basvuru_tarihi ? r.basvuru_tarihi.split('T')[0] : '',
        studentName: r.ogrenci_adi,
        parentName: r.veli_adi ?? '',
        phone: r.telefon,
        city: r.sehir ?? 'Antalya',
        grade: r.sinif ?? '',
        branch: r.talep_edilen_ders ?? '',
        lessonType: r.ders_tipi ?? 'Birebir Online',
        source: r.kaynak ?? 'Manuel',
        status: (r.durum ?? 'Yeni Başvuru') as ApplicationStatus,
        target: r.hedef ?? '',
        notes: r.notlar ?? '',
      })));
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = data.filter((a) => {
    const q = search.toLowerCase();
    const matchQ = !q || a.studentName.toLowerCase().includes(q) || a.parentName.toLowerCase().includes(q) || a.phone.includes(q);
    const matchBranch = !filterBranch || a.branch === filterBranch;
    const matchStatus = !filterStatus || a.status === filterStatus;
    const matchSource = !filterSource || a.source === filterSource;
    return matchQ && matchBranch && matchStatus && matchSource;
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const { error } = await supabase.from('egtpanel_basvurular').insert({
      ogrenci_adi: form.studentName,
      veli_adi: form.parentName || null,
      telefon: form.phone,
      sehir: form.city,
      sinif: form.grade || null,
      talep_edilen_ders: form.branch || null,
      hedef: form.target || null,
      ders_tipi: form.lessonType,
      kaynak: form.source,
      durum: form.status,
      notlar: form.notes || null,
    });

    setSaving(false);
    if (error) {
      showToast('Hata: Başvuru kaydedilemedi.');
    } else {
      showToast('Başvuru başarıyla eklendi!');
      setShowModal(false);
      setForm(emptyForm);
      fetchData();
    }
  };

  const [editItem, setEditItem] = useState<Basvuru | null>(null);
  const [editForm, setEditForm] = useState<typeof emptyForm | null>(null);
  const [deleteItem, setDeleteItem] = useState<Basvuru | null>(null);

  const autoCreateStudent = async (basvuru: Basvuru) => {
    // Aynı isim + telefon ile öğrenci zaten var mı kontrol et
    const { data: existing } = await supabase
      .from('egtpanel_ogrenciler')
      .select('id')
      .eq('ogrenci_adi', basvuru.studentName)
      .maybeSingle();
    if (existing) return; // Zaten var, tekrar ekleme

    await supabase.from('egtpanel_ogrenciler').insert({
      ogrenci_adi: basvuru.studentName,
      veli_adi: basvuru.parentName || null,
      telefon: basvuru.phone || null,
      sehir: basvuru.city || null,
      sinif: basvuru.grade || null,
      branslar: basvuru.branch ? [basvuru.branch] : [],
      ders_tipi: lessonTypeToDb(basvuru.lessonType),
      notlar: basvuru.notes || null,
      aktif: true,
    });
    showToast('Öğrenci kaydı otomatik oluşturuldu!');
  };

  const updateStatus = async (id: string, newStatus: ApplicationStatus) => {
    const { error } = await supabase
      .from('egtpanel_basvurular')
      .update({ durum: newStatus, guncelleme_tarihi: new Date().toISOString() })
      .eq('id', id);

    if (!error) {
      setData((prev) => prev.map((a) => a.id === id ? { ...a, status: newStatus } : a));
      if (detailItem?.id === id) setDetailItem((d) => d ? { ...d, status: newStatus } : null);
      showToast('Durum güncellendi.');

      // Aktif Derse Başladı yapılınca öğrenci otomatik kaydı
      if (newStatus === 'Aktif Derse Başladı') {
        const basvuru = data.find((a) => a.id === id);
        if (basvuru) await autoCreateStudent(basvuru);
      }
    }
  };

  const openEdit = (a: Basvuru) => {
    setDetailItem(null);
    setEditItem(a);
    setEditForm({ studentName: a.studentName, parentName: a.parentName, phone: a.phone, city: a.city, grade: a.grade, branch: a.branch, target: a.target, lessonType: a.lessonType, source: a.source, notes: a.notes, status: a.status });
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editItem || !editForm) return;
    setSaving(true);
    const { error } = await supabase.from('egtpanel_basvurular').update({
      ogrenci_adi: editForm.studentName, veli_adi: editForm.parentName || null,
      telefon: editForm.phone, sehir: editForm.city, sinif: editForm.grade || null,
      talep_edilen_ders: editForm.branch || null, hedef: editForm.target || null,
      ders_tipi: editForm.lessonType, kaynak: editForm.source,
      durum: editForm.status, notlar: editForm.notes || null,
      guncelleme_tarihi: new Date().toISOString(),
    }).eq('id', editItem.id);
    setSaving(false);
    if (error) { showToast('Hata: Güncellenemedi.'); return; }
    showToast('Başvuru güncellendi!');

    // Eğer durum Aktif Derse Başladı yapıldıysa öğrenciyi otomatik oluştur
    if (editForm.status === 'Aktif Derse Başladı') {
      const fakeBasvuru: Basvuru = {
        ...editItem,
        studentName: editForm.studentName,
        parentName: editForm.parentName,
        phone: editForm.phone,
        city: editForm.city,
        grade: editForm.grade,
        branch: editForm.branch,
        lessonType: editForm.lessonType,
        notes: editForm.notes,
        status: editForm.status,
      };
      await autoCreateStudent(fakeBasvuru);
    }

    setEditItem(null); setEditForm(null);
    fetchData();
  };

  const handleDelete = async () => {
    if (!deleteItem) return;
    setSaving(true);
    const { error } = await supabase.from('egtpanel_basvurular').delete().eq('id', deleteItem.id);
    setSaving(false);
    if (error) { showToast('Hata: Silinemedi.'); return; }
    showToast('Başvuru silindi.');
    setDeleteItem(null); fetchData();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-[#1e3a5f] text-white px-5 py-3 rounded-xl text-sm font-medium">
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Başvurular</h1>
          <p className="text-slate-500 text-sm mt-1">Gelen öğrenci / veli başvurularını yönetin</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-[#1e3a5f] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#162d4a] transition-colors whitespace-nowrap cursor-pointer">
          <i className="ri-add-line" />
          Yeni Başvuru Ekle
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Toplam', val: data.length, color: 'text-slate-700', bg: 'bg-slate-50' },
          { label: 'Yeni', val: data.filter((a) => a.status === 'Yeni Başvuru').length, color: 'text-blue-700', bg: 'bg-blue-50' },
          { label: 'Aktif Derse Geçen', val: data.filter((a) => a.status === 'Aktif Derse Başladı').length, color: 'text-green-700', bg: 'bg-green-50' },
          { label: 'İptal', val: data.filter((a) => a.status === 'İptal').length, color: 'text-red-700', bg: 'bg-red-50' },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4`}>
            <p className="text-xs text-slate-500 font-medium">{s.label}</p>
            <p className={`text-2xl font-bold mt-1 ${s.color}`}>{s.val}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-100 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Öğrenci, veli, telefon..." className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 focus:border-[#1e3a5f]" />
          </div>
          <select value={filterBranch} onChange={(e) => setFilterBranch(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Branşlar</option>
            {BRANCHES.map((b) => <option key={b}>{b}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Durumlar</option>
            {ALL_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
            <option value="">Tüm Kaynaklar</option>
            {['Instagram', 'WhatsApp', 'Tavsiye', 'Manuel', 'Reklam'].map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <i className="ri-loader-4-line text-3xl text-slate-300 animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  {['Tarih', 'Öğrenci', 'Veli / Tel', 'Sınıf', 'Branş', 'Ders Tipi', 'Kaynak', 'Durum', 'İşlem'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filtered.length === 0 ? (
                  <tr><td colSpan={9} className="text-center py-16">
                    <i className="ri-inbox-line text-4xl text-slate-200 block mb-2" />
                    <p className="text-slate-400 text-sm">Başvuru bulunamadı</p>
                  </td></tr>
                ) : filtered.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{a.date}</td>
                    <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">{a.studentName}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                      <div>{a.parentName}</div>
                      <div className="text-xs text-slate-400">{a.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{a.grade}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-xs font-medium">{a.branch}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap text-xs">{a.lessonType}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sourceColors[a.source] ?? 'bg-gray-100 text-gray-600'}`}>{a.source}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[a.status] ?? 'bg-gray-100 text-gray-600'}`}>{a.status}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setDetailItem(a)} className="text-[#1e3a5f] text-xs font-medium px-2.5 py-1.5 rounded-lg border border-[#1e3a5f]/20 hover:bg-[#1e3a5f]/5 transition-colors cursor-pointer whitespace-nowrap">Detay</button>
                        <button onClick={() => openEdit(a)} className="text-slate-600 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer"><i className="ri-edit-line" /></button>
                        <button onClick={() => setDeleteItem(a)} className="text-red-500 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-red-100 hover:bg-red-50 transition-colors cursor-pointer"><i className="ri-delete-bin-line" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editItem && editForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Başvuruyu Düzenle</h2>
              <button onClick={() => { setEditItem(null); setEditForm(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer"><i className="ri-close-line" /></button>
            </div>
            <form onSubmit={handleEditSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci Adı *</label><input required value={editForm.studentName} onChange={(e) => setEditForm((p) => p ? ({...p, studentName: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Veli Adı</label><input value={editForm.parentName} onChange={(e) => setEditForm((p) => p ? ({...p, parentName: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Telefon *</label><input required value={editForm.phone} onChange={(e) => setEditForm((p) => p ? ({...p, phone: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Şehir</label><input value={editForm.city} onChange={(e) => setEditForm((p) => p ? ({...p, city: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Sınıf</label><select value={editForm.grade} onChange={(e) => setEditForm((p) => p ? ({...p, grade: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"><option value="">Seçiniz</option>{GRADE_LEVELS.map((g) => <option key={g}>{g}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Branş</label><select value={editForm.branch} onChange={(e) => setEditForm((p) => p ? ({...p, branch: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"><option value="">Seçiniz</option>{BRANCHES.map((b) => <option key={b}>{b}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Ders Tipi</label><select value={editForm.lessonType} onChange={(e) => setEditForm((p) => p ? ({...p, lessonType: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">{['Birebir Online', 'Yüz Yüze', 'Grup Dersi', 'KPSS'].map((t) => <option key={t}>{t}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Kaynak</label><select value={editForm.source} onChange={(e) => setEditForm((p) => p ? ({...p, source: e.target.value}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">{['Instagram', 'WhatsApp', 'Tavsiye', 'Manuel', 'Reklam'].map((s) => <option key={s}>{s}</option>)}</select></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">Durum</label><select value={editForm.status} onChange={(e) => setEditForm((p) => p ? ({...p, status: e.target.value as ApplicationStatus}) : p)} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">{ALL_STATUSES.map((s) => <option key={s}>{s}</option>)}</select></div>
              </div>
              <div><label className="block text-xs font-medium text-slate-600 mb-1">Notlar</label><textarea value={editForm.notes} onChange={(e) => setEditForm((p) => p ? ({...p, notes: e.target.value}) : p)} rows={3} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" /></div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => { setEditItem(null); setEditForm(null); }} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] disabled:opacity-60 cursor-pointer">{saving ? 'Kaydediliyor...' : 'Güncelle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4"><i className="ri-delete-bin-line text-red-500 text-xl" /></div>
            <h3 className="font-bold text-slate-800 text-lg mb-1">Başvuruyu Sil</h3>
            <p className="text-slate-500 text-sm mb-6"><strong>{deleteItem.studentName}</strong> başvurusunu silmek istediğinizden emin misiniz?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteItem(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">İptal</button>
              <button onClick={handleDelete} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 disabled:opacity-60 cursor-pointer">{saving ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Yeni Başvuru Ekle</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Öğrenci Adı Soyadı *</label>
                  <input required value={form.studentName} onChange={(e) => setForm((p) => ({ ...p, studentName: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Veli Adı Soyadı</label>
                  <input value={form.parentName} onChange={(e) => setForm((p) => ({ ...p, parentName: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Telefon *</label>
                  <input required value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Şehir</label>
                  <input value={form.city} onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Sınıf Seviyesi *</label>
                  <select required value={form.grade} onChange={(e) => setForm((p) => ({ ...p, grade: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Seçiniz</option>
                    {GRADE_LEVELS.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Talep Edilen Branş *</label>
                  <select required value={form.branch} onChange={(e) => setForm((p) => ({ ...p, branch: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Seçiniz</option>
                    {BRANCHES.map((b) => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Hedef</label>
                  <select value={form.target} onChange={(e) => setForm((p) => ({ ...p, target: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    <option value="">Seçiniz</option>
                    {['Okula Destek', 'LGS', 'YKS', 'Eksik Kapatma', 'Sınav Hazırlık'].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Ders Tipi</label>
                  <select value={form.lessonType} onChange={(e) => setForm((p) => ({ ...p, lessonType: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    {['Birebir Online', 'Yüz Yüze', 'Grup Dersi', 'KPSS'].map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Başvuru Kaynağı</label>
                  <select value={form.source} onChange={(e) => setForm((p) => ({ ...p, source: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    {['Instagram', 'WhatsApp', 'Tavsiye', 'Manuel', 'Reklam'].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Durum</label>
                  <select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as ApplicationStatus }))} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20">
                    {ALL_STATUSES.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Notlar</label>
                <textarea value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} rows={3} maxLength={500} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 transition-colors cursor-pointer">İptal</button>
                <button type="submit" disabled={saving} className="flex-1 py-2.5 rounded-lg bg-[#1e3a5f] text-white text-sm font-medium hover:bg-[#162d4a] transition-colors cursor-pointer disabled:opacity-60">
                  {saving ? 'Kaydediliyor...' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {detailItem && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Başvuru Detayı</h2>
              <button onClick={() => setDetailItem(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-400 cursor-pointer">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-2xl font-bold text-slate-800">{detailItem.studentName}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusColors[detailItem.status] ?? ''}`}>{detailItem.status}</span>
              </div>
              {[
                { label: 'Veli', val: detailItem.parentName },
                { label: 'Telefon', val: detailItem.phone },
                { label: 'Şehir', val: detailItem.city },
                { label: 'Sınıf', val: detailItem.grade },
                { label: 'Branş', val: detailItem.branch },
                { label: 'Ders Tipi', val: detailItem.lessonType },
                { label: 'Kaynak', val: detailItem.source },
                { label: 'Hedef', val: detailItem.target },
                { label: 'Tarih', val: detailItem.date },
              ].map((r) => (
                <div key={r.label} className="flex items-center gap-2 text-sm">
                  <span className="text-slate-400 w-24">{r.label}</span>
                  <span className="text-slate-700 font-medium">{r.val || '-'}</span>
                </div>
              ))}
              {detailItem.notes && (
                <div className="bg-amber-50 rounded-lg p-3 text-sm text-amber-800 mt-2">
                  <i className="ri-sticky-note-line mr-1" /> {detailItem.notes}
                </div>
              )}
              {/* Status update */}
              <div className="pt-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Durum Güncelle</label>
                <select
                  value={detailItem.status}
                  onChange={(e) => updateStatus(detailItem.id, e.target.value as ApplicationStatus)}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/20"
                >
                  {ALL_STATUSES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex gap-3 pt-4">
                <a href={`https://wa.me/${detailItem.phone.replace(/\s/g, '')}`} target="_blank" rel="nofollow noreferrer" className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-green-500 text-white text-sm font-medium hover:bg-green-600 transition-colors">
                  <i className="ri-whatsapp-line" /> WhatsApp
                </a>
                <button onClick={() => setDetailItem(null)} className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer">Kapat</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}